import { Fragment, useEffect, useId } from 'react'
import { ChevronLeftIcon, Modal, useModal } from '$components'
import { User } from '$api-client/types.gen'
import { PersonSelectList } from './list'

export type PersonSelectMobileContentViewProps = {
  title?: string
  onBack?: () => void
  options: User[]
  selectedPeopleIds: number[]
  onSelectPerson: (person: User) => void
  onClear: () => void
}

export function PersonSelectMobileContentView({
  title = 'Select people',
  onBack,
  options,
  selectedPeopleIds,
  onSelectPerson,
  onClear,
}: PersonSelectMobileContentViewProps) {
  return (
    <Fragment>
      <Modal.Header>
        <div className="flex items-center gap-stack-tight">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="p-stack-tight -ml-stack-tight border border-transparent hover:border-border rounded-surface transition-colors"
            >
              <ChevronLeftIcon className="size-4 text-fg-muted" />
            </button>
          )}

          <Modal.Heading>
            {title}
          </Modal.Heading>
        </div>
      </Modal.Header>

      <Modal.Content className="p-0">
        <PersonSelectList
          options={options}
          selectedPeopleIds={selectedPeopleIds}
          onSelectPerson={onSelectPerson}
          onClear={onClear}
          className="max-h-[min(70vh,480px)]"
        />
      </Modal.Content>
    </Fragment>
  )
}

export type PersonSelectMobileContentProps = {
  title?: string
  isOpen: boolean
  onClose: () => void
  options: User[]
  selectedPeopleIds: number[]
  onSelectPerson: (person: User) => void
  onClear: () => void
}

export function PersonSelectMobileContent({
  title = 'Select people',
  isOpen,
  onClose,
  options,
  selectedPeopleIds,
  onSelectPerson,
  onClear,
}: PersonSelectMobileContentProps) {
  const personSelectId = useId()
  const parentModal = useModal(true)

  useEffect(() => {
    if (!parentModal || !isOpen) {
      return
    }

    parentModal.push({
      id: personSelectId,
      component: (
        <PersonSelectMobileContentView
          title={title}
          onBack={onClose}
          options={options}
          selectedPeopleIds={selectedPeopleIds}
          onSelectPerson={onSelectPerson}
          onClear={onClear}
        />
      ),
    })
    // parentModal identity changes on every stack update — sync selection into the stacked view
  }, [isOpen, personSelectId, options, selectedPeopleIds])

  useEffect(() => {
    if (!parentModal || !isOpen) {
      return
    }

    return () => {
      parentModal.pop()
    }
    // parentModal identity changes on every stack update — only react to open/close
  }, [isOpen, personSelectId])

  if (!isOpen || parentModal) {
    return null
  }

  return (
    <Modal onClose={onClose}>
      <PersonSelectMobileContentView
        title={title}
        options={options}
        selectedPeopleIds={selectedPeopleIds}
        onSelectPerson={onSelectPerson}
        onClear={onClear}
      />
    </Modal>
  )
}
